import {Inject, Prop, Ref, Vue} from 'vue-property-decorator';
import BoardPage from '@/homework/models/board-page.model';
import EdWboDrawer from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/ed-wbo-drawer';
import {BoardType} from '@/components/common/ed-board/ed-board.enum';
import KEYS from '@/values/keys';
import {_HOMEWORKS_STORE} from '@/homework/values/homeworks.values';
import {BOARD_ANNOTATION_OPTIONS, BOARD_CONFIG_PARAMETERS, BOARD_EVENTS} from '@/values/ed-whiteboard.values';
import {RESIZE_TYPES, SECTION_TYPE_CODES} from '@/core/values/ed-transparency-meeting.values';
import Component from 'vue-class-component';

@Component({})
export class BoardMixin extends Vue {
    @Prop({required: true})
    public readonly boardName: string;

    @Prop({required: false})
    public socket: any;

    @Prop({required: true})
    public readonly boardPage: BoardPage;

    @Prop({required: true})
    public readonly canSubmit: any;

    /**
     * Object (showHistory, versionHistory)
     */
    @Prop({required: false})
    public readonly history: any;

    @Inject({default: {value: 1}})
    public scale;

    /**
     * Required for PDFs only. Boards over images, videos or blank canvases do not require this prop.
     * @private
     */
    @Prop({required: false})
    public readonly documentHash: string;

    @Ref()
    public readonly boardElement: HTMLDivElement;


    @Ref()
    public readonly svgElement: any;

    @Ref()
    public readonly drawingAreaElement: SVGGElement;

    @Ref()
    public readonly cursorsElement: SVGGElement;


    public boardDimensions = {
        width: 500,
        height: 500
    };

    /**
     * The drawer object that will handle the drawing operations.
     */
    public drawer: EdWboDrawer = null;


    public loadedAnnotations: boolean = false;
    public loadedImage: boolean = false;
    public annotationVersion: number = 2;
    public metadata: any;
    public currentBoardTool: any = {};


    public domManagedElements = {
        pageContainerElement: null
    }


    get isTransparency(): boolean {
        return this.boardPage.boardType == BoardType.Transparency;
    }

    get noBackground(): boolean {
        return this.boardPage.boardType == BoardType.Whiteboard;
    }

    get isTask(): boolean {
        return this.boardPage.boardType == BoardType.Task;
    }

    get currentBoardPage(): string {
        return `${this.boardPage.boardIndex ? this.boardPage.boardIndex : this.boardPage.page}`;
    }

    get currentBoard(): string {
        return `${this.boardName}_${this.currentBoardPage}`;
    }

    get userId(): string {
        return this.$store.state.AuthStore.user.userId;
    }

    get userFullName(): string {
        return this.$store.state.AuthStore.user.fullName;
    }

    get boardParameters(): number {
        return this.$store.state.HomeworksStore.boardParameters;
    }

    get offlineMode(): boolean {
        return this.$store.state.AppStore.offlineMode;
    }

    get isPresentationStudentMode(): boolean {
        return this.$store.state.CourseStore.isPresentationStudentMode;
    }

    get isPresentationTeacherMode(): boolean {
        return this.$store.state.CourseStore.isPresentationTeacherMode;
    }

    get isPresentationMode(): boolean {
        return this.$store.state.CourseStore.isPresentationStudentMode || this.$store.state.CourseStore.isPresentationTeacherMode;
    }

    get isHostUser(): boolean {
        return this.isTransparency && this.isPresentationTeacherMode;
    }

    get isStudent(): boolean {
        return KEYS.ROLES_MAPPED.BUYER === this.$store.state.AuthStore.user.role;
    }

    get isOwner(): boolean {
        const isTeacherInPM = this.isTransparency && this.isPresentationTeacherMode;
        const isStudentInTasks = this.isTask && this.isStudent && this.canSubmit;
        return isTeacherInPM || isStudentInTasks;
    }

    get pageContainerElement(): any {
        if (!this.domManagedElements.pageContainerElement) {
            this.domManagedElements.pageContainerElement = document.getElementById(`page-container-${this.boardName}`);
        }
        return this.domManagedElements.pageContainerElement;
    }

    get presentationModeScrollInfo() {
        return this.$store.state.MeetingsStore.presentationModeScrollInfo;
    }

    get presentationModeLayoutInfo() {
        return this.$store.state.MeetingsStore.presentationModeLayoutInfo;
    }

    get loadedBoardsInDom() {
        return this.$store.state.MeetingsStore.loadedBoardsInDom;
    }

    get initialLoadedBoardsEmited() {
        return this.$store.state.MeetingsStore.initialLoadedBoardsEmited;
    }

    get isHistory() {
        return this.history != null && this.history.show !== undefined;
    }

    get currentMenuOptions() {
        return this.$store.state.CourseStore.currentMenuOptions;
    }

    setCursorBoardMutation(value: boolean) {
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_CURSOR_BOARD_ACTIVE, value);
    }

    /**
     * Resizes the SVG element whenever something is drawn near its border. This function is meant to be passed to
     * the EdWboDrawer as a messageHook only when the board has no background, because this resize logic only makes sense
     * for boards that may grow freely.
     */
    resizeBlankCanvas(msg) {
        if (msg.tool == 'Cursor') {
            return;
        }
        const x = msg.x || 0;
        const y = msg.y || 0;
        const growthThreshold = 500;
        const maxBoardSize = this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_BOARD_SIZE] || 65536;
        if (x > this.svgElement.width.baseVal.value - growthThreshold) {
            this.svgElement.width.baseVal.value = Math.min(x + growthThreshold, maxBoardSize);
        }
        if (y > this.svgElement.height.baseVal.value - growthThreshold) {
            this.svgElement.height.baseVal.value = Math.min(y + growthThreshold, maxBoardSize);
        }
    }

    resizeBoard(isZoom = false) {
        this.fixPageForImage(isZoom);
    }

    fixPageForImage(isZoom = false): void {
        this.$nextTick(() => {
            const isResizable = this.metadata && RESIZE_TYPES.includes(this.boardPage.sectionType);
            const isZoomable = this.metadata && isZoom;
            const pageIndex = this.boardPage.page - 1;
            
            if (isResizable || isZoomable) {
                const pdfImgSelector = `[id^="pdf-img_${this.documentHash}_${pageIndex + 1}"]`;
                const pageImageSelector = '.page-image';

                const imgElement = (document.querySelector(pdfImgSelector)
                    ?? document.querySelectorAll(pageImageSelector)[pageIndex]) as HTMLImageElement;
                
                if (!imgElement) {
                    return;
                }
                const imageSectionRectangle = imgElement.getBoundingClientRect();

                // Scale is used for cards, to know the current zoom. By default, in other objects, will be 1.
                imageSectionRectangle.width = imageSectionRectangle.width / this.scale.value;
                imageSectionRectangle.height = imageSectionRectangle.height / this.scale.value;

                const boardWidth = this.metadata.width ?? this.boardPage.width;
                const boardHeight = this.metadata.height ?? this.boardPage.height;

                let newScale = imageSectionRectangle.width / boardWidth;
                let newScaleY = imageSectionRectangle.height / boardHeight;

                this.setScale(newScale, newScaleY);

                if (this.boardElement) {
                    this.boardElement.style.setProperty('width', `${imageSectionRectangle.width}px`);
                    this.boardElement.style.setProperty('height', `${imageSectionRectangle.height}px`);
                    this.boardElement.style.setProperty('margin', 'auto');
                }

            } else {
                // We also have to do rescaling on regular task boards (i.e., PDFs) in split objects.
                const pdfImgSelector = `[id^="pdf-img_${this.documentHash}_${pageIndex + 1}"]`;
                const pageImageSelector = '.page-image';
                const imgElement = (document.querySelector(pdfImgSelector)
                    ?? document.querySelectorAll(pageImageSelector)[pageIndex]) as HTMLImageElement;

                if (!imgElement) {
                    return;
                }
                const imageSectionRectangle = imgElement.getBoundingClientRect();
                if (!imageSectionRectangle || imageSectionRectangle.width === 0 || imageSectionRectangle.height === 0) {
                    return;
                }
                let newScale = imageSectionRectangle.width / this.boardPage.width;
                let newScaleY = imageSectionRectangle.height / this.boardPage.height;
                this.setScale(newScale, newScaleY);
            }
        });
    }

    setScale(scale: number, scaleY?: number): number {
        this.drawer?.setScale(scale, scaleY);
        return scale;
    }

    handleMessage(message: any): Promise<any> {
        return this.drawer.handleMessage(message);
    }

    /**
     * Callback called by the drawer before each draw action.
     */
    doBeforeToolDraw(data: any, isMadeByUser: boolean): void {
        if (data && data.tool !== 'Cursor' && data.tool !== 'Eraser All' && data.tool !== 'Zoom') {
            this.drawer.manageAnnotation(data, isMadeByUser);
            this.$edEventBus.$emit(BOARD_EVENTS.DRAWING_TASK);
        }
    }

    fixPageForDoc(pageData: { isTransparency?: boolean }): void {
        if (this.noBackground) {
            this.boardElement.parentElement.style.setProperty('overflow', 'unset');
        } else {
            let newScale: number = this.pageContainerElement.offsetWidth / this.boardDimensions.width;
            this.setScale(newScale);
            const selector = `[id^="${this.boardPage.page}${this.documentHash}"]`;
            const alternateSelector = '.pdf-container__canvas';
            const imageSectionContainer = (document.querySelector(selector)
                ?? document.querySelectorAll(alternateSelector)[this.boardPage.page - 1]) as HTMLImageElement;

            // const bodyHeight: number = pageData.isTransparency && imageSectionContainer
            const bodyHeight: number = imageSectionContainer
                ? imageSectionContainer?.getBoundingClientRect()?.height
                : this.boardDimensions.height * newScale;

            if (this.boardElement) {
                this.boardElement.style.setProperty('height', `${bodyHeight}px`);
            }
        }
    }

    changeTool(toolName: string, isSecondary: boolean = false, boardPage: number) {
        // Check if the tool change is for a specific board
        if (boardPage && 0 < boardPage && boardPage.toString() != this.currentBoardPage) {
            return;
        }
        this.drawer.changeTool(toolName, isSecondary);
    }

    changeColor(colorValue: string) {
        this.drawer.setColor(colorValue);
    }


    changeToolSize(value: number): void {
        this.drawer.setSize(value);
    }

    changeToolOpacity(value: number): void {
        this.drawer.setOpacity(value);
    }

    createEvents() {
        if (this.socket) {
            this.socket.on('connect', () => this.clearPencilPathDataCache());
        }
    }

    clearPencilPathDataCache() {
        this.drawer.clearPathDataCache();
    }

    emitLoadedBoard(): void {
        this.emitBoardReady();
        if (this.loadedAnnotations && (this.noBackground || this.loadedImage) && !this.initialLoadedBoardsEmited) {

            if (!this.isPresentationMode || this.boardPage.sectionType == SECTION_TYPE_CODES.LESSON_CARD) {
                this.$edEventBus.$emit(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.boardPage.page);
            } else {
                if (this.isPresentationTeacherMode || !this.presentationModeScrollInfo.currentPageSizes
                    || !this.presentationModeScrollInfo.currentPageSizes[0]?.page || this.isTask) {
                    if (RESIZE_TYPES.includes(this.boardPage.sectionType) && this.boardPage.sectionType !== SECTION_TYPE_CODES.LESSON_PDF) {
                        const selectors = '.page-image'
                            + (this.documentHash ? `.pdf-img-${this.documentHash}` : '');
                        if (this.loadedBoardsInDom >= document.querySelectorAll(selectors).length) {
                            this.emitInitialMessagesForStudent();
                        }
                    } else {
                        this.$edEventBus.$emit(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.boardPage.page);
                    }
                } else if (this.presentationModeScrollInfo.currentPageSizes && this.isPresentationStudentMode) {
                    if (this.boardPage.page >= this.presentationModeScrollInfo.currentPageSizes[0].page) {
                        this.emitInitialMessagesForStudent();
                    }
                }
            }
        }
    }

    emitBoardReady() {
        if (this.loadedAnnotations && (this.noBackground || this.loadedImage)) {
            this.$edEventBus.$emit(BOARD_EVENTS.BOARD_READY, this.boardPage.page);
            this.addSvgClickEventListener();
        }
    }

    emitInitialMessagesForStudent() {
        setTimeout(() => {
            this.$edEventBus.$emit(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.boardPage.page);
            this.$edEventBus.$emit(BOARD_EVENTS.STUDENT_INITIAL_BOARDS_LOADED, this.presentationModeScrollInfo, true);
            this.$edEventBus.$emit(BOARD_EVENTS.UPDATE_LAYOUT, this.presentationModeLayoutInfo, true);
        }, 2000);
    }


    addSvgClickEventListener() {
        this.svgElement?.addEventListener('mousedown', this.emitBoardActivated);
    }

    removeSvgClickEventListener() {
        this.svgElement?.removeEventListener('mousedown', this.emitBoardActivated);
    }

    emitBoardActivated() {
        if (!this.noBackground) {
            this.$edEventBus.$emit(`${BOARD_EVENTS.BOARD_ACTIVATED}_${this.documentHash}`, this.boardPage.page - 1);
        }
    }

    addBoardActionToStack(stackName: string, annotatedBoards: any): void {
        if (annotatedBoards && Object.keys(annotatedBoards).length > 0) {
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_BOARD_ANNOTATIONS, {
                board: this.currentBoard,
                boardGroupHash: this.boardName,
                boardAnnotations: stackName,
                annotatedBoards
            });
            this.$edEventBus.$emit(BOARD_EVENTS.DRAWING_TASK);
        }
    }

    getLastBoardActionFromStack(stackName: string): any {
        let lastBoardAction: any = null;

        const boardActionStack = this.$store.state.HomeworksStore[stackName][this.boardName];
        if (boardActionStack && boardActionStack.length > 0) {
            lastBoardAction = boardActionStack[boardActionStack.length - 1];
        }

        return lastBoardAction;
    }

    removeLastBoardActionFromStack(stackName: string): void {
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.REMOVE_LAST_BOARD_ACTION_ANNOTATION, {
            boardAnnotations: stackName,
            boardGroupHash: this.boardName
        });
        this.$edEventBus.$emit(BOARD_EVENTS.DRAWING_TASK);
    }

    cleanBoardActionStack(stackName: string): void {
        let storeMutation: string;

        switch (stackName) {
            case BOARD_ANNOTATION_OPTIONS.undo:
                storeMutation = _HOMEWORKS_STORE.MUTATIONS.CLEAR_UNDO_BOARD_ANNOTATIONS;
                break;
            case BOARD_ANNOTATION_OPTIONS.redo:
                storeMutation = _HOMEWORKS_STORE.MUTATIONS.CLEAR_REDO_BOARD_ANNOTATIONS;
                break;
            default:
                storeMutation = undefined;
                break;
        }

        if (storeMutation) {
            this.$store.commit(storeMutation, this.boardName);
            this.$edEventBus.$emit(BOARD_EVENTS.DRAWING_TASK);
        }
    }
    getAllBoardActionsFromStack(stackName: string): any[] {
        const boardActionStack = this.$store.state.HomeworksStore[stackName][this.boardName];
        return boardActionStack ? boardActionStack : [];
    }

    canSendMessage(): boolean {
        return !this.offlineMode && this.socket && !this.socket.disconnected;
    }

    enableDefaultTool(enable: true) {
        if (enable) {
            this.changeTool(this.currentBoardTool?.name, this.currentBoardTool.isSecondary, 0)
        } else if (this.drawer.currentTool) {
            this.currentBoardTool = {
                name: this.drawer.currentTool?.name,
                isSecondary: this.drawer.currentTool?.secondary?.active
            }
            this.drawer.unsetCurrentTool();
        }
    }

    setCurrentMenuOptions() {
        const currentMenuInfo = this.currentMenuOptions[this.boardName];
        if (currentMenuInfo) {
            if (currentMenuInfo.toolName) this.changeTool(currentMenuInfo.toolName, currentMenuInfo.isSecondary, 0);
            if (currentMenuInfo.color) this.changeColor(currentMenuInfo.color);
            if (currentMenuInfo.size) this.changeToolSize(currentMenuInfo.size);
            if (currentMenuInfo.opacity) this.changeToolOpacity(currentMenuInfo.opacity);
        }
    }

}